/**
 * 業務「選定対象を引き当てる」（1955）のロジック図の見本 v2 の業務ルール（mock）。箱を押したときに開く
 * モーダルの中身である（Dhik 2026-10-06「v2 で業務ルールを出せるようにできない？基本既存のものをそのまま
 * コピーできる」）。
 *
 * - 2346 ・ 1898 ・ 1903 ・ 1904 ・ 2347 は、2026-10-06 の master の業務 1955 の業務ルール（`getBusinessRules`
 *   の応答）をそのまま写した。1903 だけ、v2 の図に合わせてマッチ度（高い順）のキーと入力を足した
 * - 9101（適合を判定する）と 9102（マッチ度を算出する）は図にしか無いので mock で作った。9xxx の番号は
 *   実在しない見本用の番号である
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1955 のロジック図の見本 v2 で、業務ルールの箱を押したときに開く業務ルール 7 件の中身（実データの写しと mock）を持つ。
 * @graph-connects none
 */
import type { BusinessRuleView, ElementRef } from '@cortex/domain-spec-console';

/** 番号と表示名から参照を作る（見本は全て訳済み）。 @graph-connects none */
function ref(id: number, label: string): ElementRef {
  return { id, label, untranslated: false };
}

/** 業務ルールの入力 1 件（読む用語と、それを出力する業務ルール）。 @graph-connects ref [calls] 参照の組み立て */
function input(
  id: number,
  label: string,
  ...suppliers: ElementRef[]
): BusinessRuleView['inputs'][number] {
  return { term: ref(id, label), suppliers };
}

/** 見本 v2 の業務ルール（図の箱が持つ番号で引く）。 @graph-connects none */
export const LOGIC_MOCK_V2_RULES: readonly BusinessRuleView[] = [
  {
    ruleId: 2346,
    label: '選定対象の選定途中のユーザーの有無を判定する',
    untranslated: false,
    kind: 'decisionTable',
    crud: 'R',
    output: ref(2343, '選定対象の選定途中のユーザーの有無'),
    inputs: [input(1878, '選定対象の選定の担当者'), input(1801, '会員の会員ステータス')],
    body: {
      kind: 'decisionTable',
      inputs: [ref(1878, '選定対象の選定の担当者'), ref(1801, '会員の会員ステータス')],
      outputs: [ref(2343, '選定対象の選定途中のユーザーの有無')],
      rows: [
        {
          id: 1,
          cells: [ref(1858, '実行したスタイリスト本人'), ref(1702, '月額会員')],
          outputs: [ref(2341, 'いる')],
        },
        {
          id: 2,
          cells: [null, null],
          outputs: [ref(2342, 'いない')],
        },
      ],
      overlapPolicy: 'firstMatch',
    },
    implementation: [],
    readBy: [ref(1904, '選定対象の引き当て結果を判定する')],
    usedByBusinesses: [ref(1955, '選定対象を引き当てる')],
  },
  {
    ruleId: 1898,
    label: '選定対象の選定可能なユーザーリストを判定する',
    untranslated: false,
    kind: 'decisionTable',
    crud: 'R',
    output: ref(1877, '選定対象の選定可能なユーザーリスト'),
    inputs: [
      input(1878, '選定対象の選定の担当者'),
      input(1801, '会員の会員ステータス'),
      input(1802, '会員のレンタル状態'),
      input(1820, '選定対象の二重配送'),
      input(1843, '会員の選定保留'),
      input(2344, '会員のサービス停止'),
      input(1880, '会員の選定の除外'),
      input(1883, '選定対象の選定希望日の有無'),
    ],
    body: {
      kind: 'decisionTable',
      inputs: [
        ref(1878, '選定対象の選定の担当者'),
        ref(1801, '会員の会員ステータス'),
        ref(1802, '会員のレンタル状態'),
        ref(1820, '選定対象の二重配送'),
        ref(1843, '会員の選定保留'),
        ref(2344, '会員のサービス停止'),
        ref(1880, '会員の選定の除外'),
        ref(1883, '選定対象の選定希望日の有無'),
      ],
      outputs: [ref(1877, '選定対象の選定可能なユーザーリスト')],
      rows: [
        {
          id: 1,
          cells: [null, null, null, null, ref(1745, '保留中'), null, null, null],
          outputs: [ref(2351, '0 件')],
        },
        {
          id: 2,
          cells: [null, null, null, null, null, ref(1591, '該当する'), null, null],
          outputs: [ref(2351, '0 件')],
        },
        {
          id: 3,
          cells: [null, null, null, null, null, null, ref(1591, '該当する'), null],
          outputs: [ref(2351, '0 件')],
        },
        {
          id: 4,
          cells: [ref(1859, '別のスタイリスト'), null, null, null, null, null, null, null],
          outputs: [ref(2351, '0 件')],
        },
        {
          id: 5,
          cells: [null, null, null, null, null, null, null, ref(1710, '該当しない')],
          outputs: [ref(2351, '0 件')],
        },
        {
          id: 6,
          cells: [
            ref(1857, '担当者なし'),
            ref(1702, '月額会員'),
            ref(1594, '選定待ち'),
            null,
            null,
            null,
            null,
            null,
          ],
          outputs: [ref(2350, '1 件以上')],
        },
        {
          id: 7,
          cells: [
            ref(1857, '担当者なし'),
            ref(1702, '月額会員'),
            null,
            ref(1591, '該当する'),
            null,
            null,
            null,
            null,
          ],
          outputs: [ref(2350, '1 件以上')],
        },
        {
          id: 8,
          cells: [null, null, null, null, null, null, null, null],
          outputs: [ref(2351, '0 件')],
        },
      ],
      overlapPolicy: 'firstMatch',
    },
    implementation: [],
    readBy: [ref(1903, '選定対象を並べる'), ref(1904, '選定対象の引き当て結果を判定する')],
    usedByBusinesses: [ref(1955, '選定対象を引き当てる')],
  },
  {
    ruleId: 9101,
    label: 'スタイリスト属性と会員属性から適合を判定する',
    untranslated: false,
    kind: 'decisionTable',
    crud: 'R',
    output: ref(9201, '属性 A の適合度'),
    inputs: [input(9211, 'スタイリストの属性 A'), input(9212, '会員の属性 A')],
    body: {
      kind: 'decisionTable',
      inputs: [ref(9211, 'スタイリストの属性 A'), ref(9212, '会員の属性 A')],
      outputs: [ref(9201, '属性 A の適合度')],
      rows: [
        {
          id: 1,
          cells: [ref(9221, 'あり'), ref(9221, 'あり')],
          outputs: [ref(9231, '1')],
        },
        {
          id: 2,
          cells: [ref(9221, 'あり'), ref(9222, 'なし')],
          outputs: [ref(9232, '0')],
        },
        {
          id: 3,
          cells: [ref(9222, 'なし'), ref(9221, 'あり')],
          outputs: [ref(9232, '0')],
        },
        {
          id: 4,
          cells: [ref(9222, 'なし'), ref(9222, 'なし')],
          outputs: [ref(9231, '1')],
        },
      ],
      overlapPolicy: 'none',
    },
    implementation: [],
    readBy: [ref(9102, 'スタイリスト属性と会員属性からマッチ度を算出する')],
    usedByBusinesses: [ref(1955, '選定対象を引き当てる')],
  },
  {
    ruleId: 9102,
    label: 'スタイリスト属性と会員属性からマッチ度を算出する',
    untranslated: false,
    kind: 'formula',
    crud: 'R',
    output: ref(2321, '選定対象のマッチ度'),
    inputs: [
      input(9251, '属性の適合度の重み A'),
      input(9201, '属性 A の適合度', ref(9101, 'スタイリスト属性と会員属性から適合を判定する')),
      input(9252, '属性の適合度の重み B'),
      input(9202, '属性 B の適合度', ref(9101, 'スタイリスト属性と会員属性から適合を判定する')),
    ],
    body: {
      kind: 'formula',
      output: ref(2321, '選定対象のマッチ度'),
      steps: [
        {
          id: 1,
          name: ref(9241, '属性 A の加点'),
          operator: 'multiply',
          operands: [
            { kind: 'term', term: ref(9251, '属性の適合度の重み A'), when: null },
            { kind: 'term', term: ref(9201, '属性 A の適合度'), when: null },
          ],
        },
        {
          id: 2,
          name: ref(9242, '属性 B の加点'),
          operator: 'multiply',
          operands: [
            { kind: 'term', term: ref(9252, '属性の適合度の重み B'), when: null },
            { kind: 'term', term: ref(9202, '属性 B の適合度'), when: null },
          ],
        },
        {
          id: 3,
          name: ref(2321, '選定対象のマッチ度'),
          operator: 'add',
          operands: [
            { kind: 'step', step: 1 },
            { kind: 'step', step: 2 },
          ],
        },
      ],
    },
    implementation: [],
    readBy: [ref(1903, '選定対象を並べる')],
    usedByBusinesses: [ref(1955, '選定対象を引き当てる')],
  },
  {
    ruleId: 1903,
    label: '選定対象を並べる',
    untranslated: false,
    kind: 'sort',
    crud: 'R',
    output: ref(1896, '選定対象の並び'),
    inputs: [
      input(1546, '選定対象'),
      input(1820, '選定対象の二重配送'),
      input(1882, '選定対象の選定希望日'),
      input(
        1877,
        '選定対象の選定可能なユーザーリスト',
        ref(1898, '選定対象の選定可能なユーザーリストを判定する'),
      ),
      input(1884, '選定対象の選定希望日と実行日の前後'),
      input(1886, '選定対象の指名スタイリストの有無'),
      input(1887, '選定対象の指名グループの有無'),
      input(1889, '選定の運用設定の初回選定優先モードの切替'),
      input(1890, 'スタイリストの初回選定の得意'),
      input(1891, '選定対象の初回選定'),
      input(1893, '選定対象の基本の配送優先度'),
      input(1894, '選定対象の配送優先度の引き上げ'),
      input(
        2321,
        '選定対象のマッチ度',
        ref(9102, 'スタイリスト属性と会員属性からマッチ度を算出する'),
      ),
    ],
    body: {
      kind: 'sort',
      population: ref(1546, '選定対象'),
      keys: [
        {
          rank: 1,
          attribute: ref(2345, '選定対象の選定可能'),
          order: [ref(1591, '該当する'), ref(1710, '該当しない')],
          whenMissing: 'exclude',
        },
        {
          rank: 2,
          attribute: ref(1885, '選定対象の選定希望日の状況'),
          order: [ref(1866, '期限超過'), ref(1867, '当日'), ref(1868, '未来')],
          whenMissing: 'exclude',
        },
        {
          rank: 3,
          attribute: ref(1888, '選定対象の指名の区分'),
          order: [ref(1869, '個人指名'), ref(1870, 'グループ指名'), ref(1871, '指名なし')],
          whenMissing: 'last',
        },
        {
          rank: 4,
          attribute: ref(1892, '選定対象の初回選定の寄せ'),
          order: [ref(1872, '前へ'), ref(1873, '変えない'), ref(1874, '後へ')],
          whenMissing: 'last',
        },
        {
          rank: 5,
          attribute: ref(1820, '選定対象の二重配送'),
          order: [ref(1591, '該当する'), ref(1710, '該当しない')],
          whenMissing: 'last',
        },
        {
          rank: 6,
          attribute: ref(1895, '選定対象の配送優先度'),
          order: 'desc',
          whenMissing: 'last',
        },
        { rank: 7, attribute: ref(2321, '選定対象のマッチ度'), order: 'desc', whenMissing: 'last' },
        {
          rank: 8,
          attribute: ref(1882, '選定対象の選定希望日'),
          order: 'earliest',
          whenMissing: 'exclude',
        },
      ],
      tieBreak: 'undefined',
      take: 1,
      derive: [
        {
          key: ref(2345, '選定対象の選定可能'),
          body: {
            kind: 'decisionTable',
            inputs: [ref(1877, '選定対象の選定可能なユーザーリスト')],
            rows: [
              {
                id: 1,
                cells: [ref(2350, '1 件以上')],
                output: ref(1591, '該当する'),
              },
            ],
            overlapPolicy: 'none',
          },
        },
        {
          key: ref(1885, '選定対象の選定希望日の状況'),
          body: {
            kind: 'decisionTable',
            inputs: [ref(1884, '選定対象の選定希望日と実行日の前後')],
            rows: [
              {
                id: 1,
                cells: [ref(1863, '実行日より前')],
                output: ref(1866, '期限超過'),
              },
              {
                id: 2,
                cells: [ref(1864, '実行日と同じ日')],
                output: ref(1867, '当日'),
              },
              {
                id: 3,
                cells: [ref(1865, '実行日より後')],
                output: ref(1868, '未来'),
              },
            ],
            overlapPolicy: 'none',
          },
        },
        {
          key: ref(1888, '選定対象の指名の区分'),
          body: {
            kind: 'decisionTable',
            inputs: [
              ref(1886, '選定対象の指名スタイリストの有無'),
              ref(1887, '選定対象の指名グループの有無'),
            ],
            rows: [
              {
                id: 1,
                cells: [ref(1591, '該当する'), null],
                output: ref(1869, '個人指名'),
              },
              {
                id: 2,
                cells: [ref(1710, '該当しない'), ref(1591, '該当する')],
                output: ref(1870, 'グループ指名'),
              },
              {
                id: 3,
                cells: [ref(1710, '該当しない'), ref(1710, '該当しない')],
                output: ref(1871, '指名なし'),
              },
            ],
            overlapPolicy: 'none',
          },
        },
        {
          key: ref(1892, '選定対象の初回選定の寄せ'),
          body: {
            kind: 'decisionTable',
            inputs: [
              ref(1889, '選定の運用設定の初回選定優先モードの切替'),
              ref(1890, 'スタイリストの初回選定の得意'),
              ref(1891, '選定対象の初回選定'),
            ],
            rows: [
              {
                id: 1,
                cells: [ref(1771, 'オフ'), null, null],
                output: ref(1873, '変えない'),
              },
              {
                id: 2,
                cells: [ref(1772, 'オン'), ref(1591, '該当する'), ref(1591, '該当する')],
                output: ref(1872, '前へ'),
              },
              {
                id: 3,
                cells: [ref(1772, 'オン'), ref(1710, '該当しない'), ref(1591, '該当する')],
                output: ref(1874, '後へ'),
              },
              {
                id: 4,
                cells: [ref(1772, 'オン'), null, ref(1710, '該当しない')],
                output: ref(1873, '変えない'),
              },
            ],
            overlapPolicy: 'none',
          },
        },
        {
          key: ref(1895, '選定対象の配送優先度'),
          body: {
            kind: 'formula',
            steps: [
              {
                id: 1,
                name: ref(1895, '選定対象の配送優先度'),
                operator: 'add',
                operands: [
                  { kind: 'term', term: ref(1893, '選定対象の基本の配送優先度'), when: null },
                  { kind: 'term', term: ref(1894, '選定対象の配送優先度の引き上げ'), when: null },
                ],
              },
            ],
          },
        },
      ],
    },
    implementation: [],
    readBy: [],
    usedByBusinesses: [ref(1955, '選定対象を引き当てる')],
  },
  {
    ruleId: 1904,
    label: '選定対象の引き当て結果を判定する',
    untranslated: false,
    kind: 'decisionTable',
    crud: 'R',
    output: ref(1897, '選定対象の引き当て結果'),
    inputs: [
      input(
        2343,
        '選定対象の選定途中のユーザーの有無',
        ref(2346, '選定対象の選定途中のユーザーの有無を判定する'),
      ),
      input(
        1877,
        '選定対象の選定可能なユーザーリスト',
        ref(1898, '選定対象の選定可能なユーザーリストを判定する'),
      ),
    ],
    body: {
      kind: 'decisionTable',
      inputs: [
        ref(2343, '選定対象の選定途中のユーザーの有無'),
        ref(1877, '選定対象の選定可能なユーザーリスト'),
      ],
      outputs: [ref(1897, '選定対象の引き当て結果')],
      rows: [
        {
          id: 1,
          cells: [ref(2341, 'いる'), null],
          outputs: [ref(1753, 'やりかけの続き')],
        },
        {
          id: 2,
          cells: [null, ref(2350, '1 件以上')],
          outputs: [ref(1592, '引き当てた')],
        },
        {
          id: 3,
          cells: [null, ref(2351, '0 件')],
          outputs: [ref(1708, '対象なし')],
        },
      ],
      overlapPolicy: 'firstMatch',
    },
    implementation: [],
    readBy: [],
    usedByBusinesses: [ref(1955, '選定対象を引き当てる')],
  },
  {
    ruleId: 2347,
    label: '選定対象の選定の状態を記録する',
    untranslated: false,
    kind: 'mutation',
    crud: 'U',
    output: ref(1806, '選定対象の選定の状態'),
    inputs: [],
    body: { kind: 'mutation', target: ref(1546, '選定対象'), crud: 'U', to: ref(1688, '選定中') },
    implementation: [],
    readBy: [],
    usedByBusinesses: [ref(1955, '選定対象を引き当てる')],
  },
];
